import { useEffect, useState } from 'react';
import { Columns2, Maximize2, Minimize2, Minus, PictureInPicture2, Pin, SquareArrowOutUpRight } from 'lucide-react';
import { useSidecarStore } from '../../../store/useSidecarStore';
import {
  isDocumentPictureInPictureSupported,
  openMessagingInNewTab,
} from '../../../store/sidecarBehavior';
import {
  closeSidecarPipWindow,
  getSidecarPipWindow,
  isSidecarPipFullscreen,
  openSidecarPipWindow,
  toggleSidecarPipFullscreen,
} from '../../../store/sidecarPip';
import { cn } from '../../../lib/cn';

const btn =
  'p-1 rounded hover:bg-neutral-100 text-cime-text-muted hover:text-cime-primary';

export function SidecarHeaderActions() {
  const close = useSidecarStore((s) => s.close);
  const pinned = useSidecarStore((s) => s.pinned);
  const pipActive = useSidecarStore((s) => s.pipActive);
  const listMode = useSidecarStore((s) => s.listMode);
  const togglePin = useSidecarStore((s) => s.togglePin);
  const toggleListMode = useSidecarStore((s) => s.toggleListMode);
  const pipSupported = isDocumentPictureInPictureSupported(window);
  const [pipFullscreen, setPipFullscreen] = useState(false);

  useEffect(() => {
    if (!pipActive) {
      setPipFullscreen(false);
      return;
    }
    const pip = getSidecarPipWindow();
    if (!pip) return;
    const sync = () => setPipFullscreen(isSidecarPipFullscreen());
    sync();
    pip.document.addEventListener('fullscreenchange', sync);
    pip.addEventListener('resize', sync);
    return () => {
      pip.document.removeEventListener('fullscreenchange', sync);
      pip.removeEventListener('resize', sync);
    };
  }, [pipActive]);

  async function onTogglePip() {
    if (useSidecarStore.getState().pipActive) {
      closeSidecarPipWindow();
      useSidecarStore.getState().exitPip();
      return;
    }
    try {
      await openSidecarPipWindow();
    } catch {
      // Gesture rejected or browser refused — stay in-page.
    }
  }

  function onOpenTab() {
    const channelId = useSidecarStore.getState().activeChannelId;
    openMessagingInNewTab(channelId);
    closeSidecarPipWindow();
    close();
  }

  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        onClick={onOpenTab}
        title="Ouvrir dans un nouvel onglet — fenêtre normale, détachable"
        aria-label="Ouvrir la messagerie dans un nouvel onglet"
        className={btn}
      >
        <SquareArrowOutUpRight className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={toggleListMode}
        title={
          listMode === 'split'
            ? 'Masquer la liste dans la conversation'
            : 'Liste toujours visible'
        }
        aria-pressed={listMode === 'split'}
        aria-label={
          listMode === 'split'
            ? 'Passer en vue empilée'
            : 'Afficher la liste en permanence'
        }
        className={cn(btn, listMode === 'split' && 'text-cime-primary')}
      >
        <Columns2 className="w-3.5 h-3.5" />
      </button>
      {pipActive && (
        <button
          type="button"
          onClick={() => {
            void toggleSidecarPipFullscreen().then(setPipFullscreen);
          }}
          title={pipFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          aria-pressed={pipFullscreen}
          aria-label={pipFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
          className={cn(btn, pipFullscreen && 'text-cime-primary')}
        >
          {pipFullscreen
            ? <Minimize2 className="w-3.5 h-3.5" />
            : <Maximize2 className="w-3.5 h-3.5" />}
        </button>
      )}
      {pipSupported && (
        <button
          type="button"
          onClick={() => void onTogglePip()}
          title={
            pipActive
              ? 'Revenir dans la page'
              : 'Picture-in-Picture — fenêtre toujours au premier plan'
          }
          aria-pressed={pipActive}
          aria-label={
            pipActive
              ? 'Fermer le Picture-in-Picture'
              : 'Ouvrir la messagerie en Picture-in-Picture'
          }
          className={cn(btn, pipActive && 'text-cime-primary')}
        >
          <PictureInPicture2 className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        type="button"
        onClick={togglePin}
        title={pinned ? 'Désépingler' : 'Épingler — rester ouvert en naviguant'}
        aria-pressed={pinned}
        aria-label={pinned ? 'Désépingler la messagerie' : 'Épingler la messagerie'}
        className={cn(btn, pinned && 'text-cime-primary')}
      >
        <Pin className="w-3.5 h-3.5" fill={pinned ? 'currentColor' : 'none'} />
      </button>
      <button
        type="button"
        onClick={close}
        title="Réduire"
        className={btn}
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
